import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/design/components/Button';
import { EmptyState, Skeleton } from '@/design/components/Display';

/**
 * A screen that needs a permission: without it, a friendly page instead of a list of 403 errors. The server still
 * checks every request; this only spares the person a screen they cannot use.
 */
export function RequirePermission({
  anyOf,
  unrestricted,
  children,
}: {
  anyOf: string[];
  /** The screen spans every record: a grant limited to assigned records is not enough. */
  unrestricted?: boolean;
  children: ReactNode;
}) {
  const { can, me } = useAuth();
  if (!me) return <Skeleton className="mx-auto h-72 max-w-5xl" />;
  const allowed = anyOf.some((c) => (unrestricted ? can(c, null) : can(c)));
  if (allowed) return <>{children}</>;
  return (
    <div className="mx-auto max-w-xl pt-10">
      <EmptyState
        icon={<ShieldAlert className="size-6" />}
        title="Esta sección no está habilitada para vos"
        action={
          <Link to="/inicio">
            <Button variant="secondary">Volver al inicio</Button>
          </Link>
        }
      >
        Si la necesitás, pedile a quien administra el sistema que te sume el permiso desde Usuarios y roles.
      </EmptyState>
    </div>
  );
}

/** Route helper: wraps a lazily loaded screen in its permission check. */
export function guarded(anyOf: string[], load: () => Promise<{ Component: React.ComponentType }>, unrestricted?: boolean) {
  return () =>
    load().then(({ Component }) => ({
      Component: () => (
        <RequirePermission anyOf={anyOf} unrestricted={unrestricted}>
          <Component />
        </RequirePermission>
      ),
    }));
}
